import type { Playlist, Video } from 'api';
import cx from 'classnames';
import { Text } from 'components/@tedui';
import CardMedia from 'components/@tedui/Card/CardMedia';
import { Link } from 'components/router';
import { formatDistanceToNowStrict } from 'date-fns';
import mixpanel from 'lib/analytics/mixpanel';
import { useAuthenticated } from 'lib/auth';
import { ViewCount } from 'lib/business';
import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';
import type { SailthruFormattedItem } from 'lib/sailthru';
import { abbreviateNumber } from 'lib/util';
import { has } from 'lodash-es';
import dynamic from 'next/dynamic';
import type { LINK_TYPE } from '../../HeroSlider';

const AnimatedIconButton = dynamic(
  () => import('components/AnimatedIconButton'),
  {
    ssr: false
  }
);

type PlaylistItem = Playlist & {
  isPlaylist: true;
};

interface NumberedCardProps {
  item: Video | PlaylistItem | SailthruFormattedItem;
  itemNumber: number;
  mixpanelTitle: string;
}

const NumberedCard = ({
  item,
  itemNumber,
  mixpanelTitle
}: NumberedCardProps) => {
  const loggedIn = useAuthenticated();

  // Check if this is playlist data
  const isPlaylist = Boolean('isPlaylist' in item && item.isPlaylist);

  // Handle different data structures for talks vs playlists
  const topic = isPlaylist
    ? null
    : 'topics' in item
      ? item?.topics?.nodes?.[0]?.name
      : null;

  const topicLink = topic ? `/talks?sort=relevance&topics%5B0%5D=${topic}` : '';

  const cardLink = isPlaylist
    ? `/playlists/${item.id}/${item.slug}`
    : `/talks/${item.slug}`;

  const trackClick = (linkType: LINK_TYPE, itemNumber: number) => {
    mixpanel.track('homepage_click', {
      current_url: window.location.href,
      title: mixpanelTitle,
      item_number: String(itemNumber),
      link_type: linkType,
      value: ''
    });
  };

  const isMobileWidth = useIsBreakpointWidth({
    size: 'md',
    breakPointType: 'tui'
  });

  const numberClasses = cx({
    'w-18 text-center': isMobileWidth && !isPlaylist,
    'px-10': !isMobileWidth && !isPlaylist,
    'pr-4 md-tui:px-5 xl-tui:pr-4': isPlaylist
  });

  return (
    <div>
      <div
        className={cx(
          'flex h-full items-center rounded-sm py-6 sm:py-5 md:py-5 xs-tui:bg-white md-tui:bg-[#F6F6F6]',
          {
            'gap-4 md-tui:gap-5 xl-tui:gap-4': isPlaylist
          }
        )}
      >
        <Link href={cardLink} isZenithLink>
          <Text
            tag="p"
            variant="header2"
            UNSAFE_className={numberClasses}
            useNewTextStyles
          >
            {itemNumber}
          </Text>
        </Link>

        <div
          className={cx('grid w-full grid-cols-1 gap-1', {
            'pr-0 sm-tui:pr-8 lg-tui:pr-20': !isPlaylist
          })}
        >
          <div
            className={cx('flex items-center', {
              'justify-end': isPlaylist,
              'justify-between': !isPlaylist
            })}
          >
            {topic && (
              <Link
                href={topicLink}
                isZenithLink
                onClick={() => trackClick('topic', itemNumber)}
              >
                <Text
                  tag="p"
                  weight="font-semibold"
                  variant="label1"
                  color={{ override: '#EB0028' }}
                  useNewTextStyles
                >
                  {topic}
                </Text>
              </Link>
            )}

            {loggedIn && (
              <AnimatedIconButton
                talk={item}
                mixpanelTitle={mixpanelTitle}
                itemNumber={String(itemNumber)}
              />
            )}
          </div>

          <Link
            href={cardLink}
            isZenithLink
            onClick={() => trackClick('title', itemNumber)}
            className="pr-5"
          >
            <Text
              tag="p"
              weight="font-semibold"
              variant="subheader1"
              useNewTextStyles
            >
              {item.title}
            </Text>

            <Text
              tag="p"
              variant={isPlaylist ? 'label1' : 'caption1'}
              useNewTextStyles
              color={{ override: isPlaylist ? '#828282' : '#767676' }}
              UNSAFE_className="pt-2"
            >
              {isPlaylist ? (
                'videos' in item ? (
                  `${item.videos.totalCount} talk${item.videos.totalCount !== 1 ? 's' : ''}`
                ) : (
                  '0 talks'
                )
              ) : (
                <>
                  {'presenterDisplayName' in item && item?.presenterDisplayName}{' '}
                  ·{' '}
                  {has(item, 'viewedCount') && (
                    <ViewCount count={item.viewedCount as number}>
                      {`${abbreviateNumber(item.viewedCount as number)}`} plays
                      ·{' '}
                    </ViewCount>
                  )}
                  {has(item, 'publishedAt') && (
                    <>
                      {formatDistanceToNowStrict(new Date(item!.publishedAt))}{' '}
                      ago
                    </>
                  )}
                </>
              )}
            </Text>
          </Link>
        </div>

        {/* Playlist Image - only show for playlists */}
        {isPlaylist && (
          <div className="relative aspect-square size-18 md-tui:mr-5 md-tui:size-35">
            <CardMedia
              image={{
                src: `${item.primaryImageSet?.[0]?.url}`,
                alt: item.title,
                aspect: 'none',
                sizes: '(max-width: 768px) 136px, 74px'
              }}
              badges={{
                ...(!isMobileWidth && {
                  bottomRight: {
                    text: `${'videos' in item ? item.videos.totalCount : 0} TALKS`
                  }
                })
              }}
              testId={`playlist-image-${item.slug}`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NumberedCard;
